import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Trash2, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetDepreciation = ({ asset, isAdmin, onRefresh }) => {
  const { t } = useTranslation();
  const [calcOpen, setCalcOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [calculating, setCalculating] = useState(false);

  const methodLabel = (m) => t(`inv.depreciation.method.${m === 'straight_line' ? 'straightLine' : 'reducingBalance'}`);

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!periodStart || !periodEnd) { toast.error(t('inv.depreciation.bothDates')); return; }
    setCalculating(true);
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/depreciation/calculate?period_start=${periodStart}&period_end=${periodEnd}`);
      toast.success(t('inv.depreciation.calculated'));
      setCalcOpen(false);
      setPeriodStart('');
      setPeriodEnd('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.depreciation.calcFailed')); }
    setCalculating(false);
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/depreciation/${entryId}`);
      toast.success(t('inv.depreciation.entryDeleted'));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.failed')); }
  };

  const entries = asset.depreciation_entries || [];
  const totalDepreciation = entries.reduce((sum, e) => sum + (e.amount || 0), 0);
  const bookValue = (asset.purchase_value || 0) - totalDepreciation;

  const canDepreciate = asset.depreciation_method && asset.purchase_value > 0;

  return (
    <div className="space-y-3" data-testid="asset-depreciation">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h4 className="text-sm font-semibold">{t('inv.depreciation.title', { count: entries.length })}</h4>
          {asset.purchase_value > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('inv.depreciation.original')}: {asset.purchase_value?.toLocaleString()} {asset.purchase_currency} |{' '}
              {t('inv.depreciation.depreciated')}: {totalDepreciation.toLocaleString()} |{' '}
              {t('inv.depreciation.bookValue')}: <span className="font-medium">{bookValue.toLocaleString()} {asset.purchase_currency}</span>
            </p>
          )}
        </div>
        {isAdmin && canDepreciate && (
          <Button size="sm" onClick={() => setCalcOpen(true)} data-testid="calculate-depreciation-btn">
            <Calculator className="w-4 h-4 mr-1" /> {t('inv.depreciation.calculate')}
          </Button>
        )}
      </div>

      {!canDepreciate && (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.depreciation.setMethodFirst')}</p>
      )}

      {entries.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('inv.depreciation.period')}</TableHead>
              <TableHead>{t('inv.depreciation.methodLabel')}</TableHead>
              <TableHead className="text-right">{t('inv.common.amount')}</TableHead>
              <TableHead>{t('common.notes')}</TableHead>
              {isAdmin && <TableHead className="w-[40px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id} data-testid={`depreciation-row-${e.id}`}>
                <TableCell className="text-sm">{e.period_start} – {e.period_end}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{methodLabel(e.method)}</Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{e.amount?.toLocaleString()}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.notes || '-'}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(e.id)} data-testid={`delete-depreciation-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent data-testid="calculate-depreciation-dialog">
          <DialogHeader><DialogTitle>{t('inv.depreciation.dialogTitle')}</DialogTitle></DialogHeader>
          <form onSubmit={handleCalculate} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('inv.depreciation.methodLabel')}: <strong>{methodLabel(asset.depreciation_method)}</strong>
              {asset.depreciation_method === 'straight_line'
                && ` | ${t('inv.depreciation.life', { years: asset.useful_life_years })}`}
              {asset.depreciation_method === 'reducing_balance'
                && ` | ${t('inv.depreciation.rate', { rate: asset.depreciation_rate })}`}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('inv.depreciation.periodStart')} *</Label>
                <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required data-testid="depreciation-start" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.depreciation.periodEnd')} *</Label>
                <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required data-testid="depreciation-end" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCalcOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" disabled={calculating} data-testid="submit-depreciation-btn">
                {calculating ? t('inv.depreciation.calculating') : t('inv.depreciation.calculate')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDepreciation;
